import { useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';

import { ActivationChannelMessages } from './ActivationChannelMessages';
import { CHANNEL_LABELS, EocLocationField } from './HwcgEocActionPanel';
import {
  HWCG_EOC_CHANNELS,
  formatHwcgReportToTime,
  hwcgActivationDefaultMessage,
  hwcgResponsiblePartyActivateScript,
  parseHwcgReportToTime,
  stripHwcgGroupActionsLinkToken,
  stripHwcgGroupReplyPrompts,
  type HwcgEocChannel,
  type HwcgEocNoticeState,
} from './hwcgEocNotice';
import type { HwcgIncidentSpecificsState } from './hwcgIncidentSpecifics';

type ScriptSpecifics = Pick<
  HwcgIncidentSpecificsState,
  'time' | 'date' | 'companyName' | 'wellName' | 'blockNumber' | 'waterDepthFt'
>;

/** Above the full-screen create-incident dialog, same raise as its other menus. */
const PICKER_LAYER = 'tw:z-[60]';

function timeOf(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function withTime(day: Date, time: string): Date {
  const [hours = '00', minutes = '00'] = time.split(':');
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    Number(hours),
    Number(minutes)
  );
}

/**
 * Date and time the person should arrive, written into the activation script as
 * "05:30 PM on 10/29/2026". The header mirrors the report-to location field
 * beside it so both controls line up.
 */
function ReportToTimeField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const picked = parseHwcgReportToTime(value);
  const [pendingTime, setPendingTime] = useState('');
  const time = picked ? timeOf(picked) : pendingTime;

  return (
    <div className="tw:grid tw:content-start tw:gap-1.5">
      <div className="tw:flex tw:items-center tw:gap-2">
        <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          Report-to time <span className="tw:text-destructive">*</span>
        </div>
        <span className="tw:text-[10px] tw:font-medium tw:text-orange-500">
          Activation script
        </span>
      </div>
      <p className="tw:text-xs tw:text-muted-foreground">
        {value.trim() && !picked
          ? `Currently "${value.trim()}". Pick a date and time to replace it.`
          : 'Uses your browser timezone.'}
      </p>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <DatePicker
          id={id}
          value={picked ?? undefined}
          placeholder="Select date"
          aria-label="Report-to date"
          positionerClassName={PICKER_LAYER}
          onChange={(day) => {
            if (!day) {
              onChange('');
              return;
            }
            onChange(formatHwcgReportToTime(withTime(day, time || '00:00')));
            setPendingTime('');
          }}
        />
        <TimePicker
          id={`${id}-time`}
          value={time}
          aria-label="Report-to time"
          positionerClassName={PICKER_LAYER}
          onChange={(next) => {
            if (!picked) {
              setPendingTime(next);
              return;
            }
            onChange(formatHwcgReportToTime(withTime(picked, next)));
          }}
        />
      </div>
    </div>
  );
}

/**
 * Optional activation-request editor shared by create incident and the live
 * roster. The checkbox label differs: create incident opts into sending, the
 * live roster opts into replacing the saved notice.
 */
export function HwcgRosterActivationFields({
  enabled,
  onEnabledChange,
  notice,
  onNoticeChange,
  specifics,
  checkboxLabel,
  idPrefix,
  template: templateOverride,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  notice: HwcgEocNoticeState;
  onNoticeChange: (next: HwcgEocNoticeState) => void;
  specifics: ScriptSpecifics;
  checkboxLabel: string;
  idPrefix: string;
  /** Default activation script; the HWCG well script when omitted. */
  template?: string;
}) {
  const hwcgMessages = templateOverride === undefined;
  const template =
    templateOverride ??
    hwcgResponsiblePartyActivateScript(
      specifics,
      notice.locationLabel,
      notice.reportToTime
    );
  const patch = (next: Partial<HwcgEocNoticeState>) => {
    onNoticeChange({ ...notice, ...next });
  };
  const toggleChannel = (channel: HwcgEocChannel, on: boolean) => {
    const channels = on
      ? [...notice.channels, channel]
      : notice.channels.filter((current) => current !== channel);
    patch({ channels });
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:border-border tw:p-3">
      <label className="tw:flex tw:items-start tw:gap-2 tw:text-sm">
        <Checkbox
          checked={enabled}
          onCheckedChange={(value) => onEnabledChange(value === true)}
        />
        <span>{checkboxLabel}</span>
      </label>
      {enabled ? (
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
            <EocLocationField
              companyName={specifics.companyName}
              label={notice.locationLabel}
              mode="report-to"
              onCommit={(next) => patch(next)}
            />
            <ReportToTimeField
              id={`${idPrefix}-report-to-time`}
              value={notice.reportToTime}
              onChange={(reportToTime) => patch({ reportToTime })}
            />
          </div>
          {hwcgMessages ? (
            <ActivationChannelMessages
              idPrefix={idPrefix}
              channels={notice.channels}
              onChannelsChange={(channels) => patch({ channels })}
              messages={notice.activationMessages ?? {}}
              onMessagesChange={(activationMessages) =>
                patch({ activationMessages })
              }
              defaultMessage={hwcgActivationDefaultMessage(
                notice.activateScript,
                template
              )}
              defaultSubject="<Position> activation notice"
            />
          ) : (
            <>
              <div className="tw:grid tw:gap-1.5">
                <Label htmlFor={`${idPrefix}-activation-script`}>
                  Activation script
                </Label>
                <textarea
                  id={`${idPrefix}-activation-script`}
                  aria-label="Activation script"
                  className="tw:min-h-28 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-2 tw:text-sm"
                  value={notice.activateScript ?? template}
                  onChange={(event) => {
                    const cleaned = stripHwcgGroupReplyPrompts(
                      stripHwcgGroupActionsLinkToken(event.target.value)
                    );
                    patch({
                      activateScript: cleaned === template ? null : cleaned,
                    });
                  }}
                />
              </div>
              <div className="tw:grid tw:gap-1.5">
                <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                  Communication method
                </div>
                <div className="tw:flex tw:flex-wrap tw:gap-3">
                  {HWCG_EOC_CHANNELS.map((channel) => (
                    <label
                      key={channel}
                      className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
                    >
                      <Checkbox
                        checked={notice.channels.includes(channel)}
                        onCheckedChange={(value) =>
                          toggleChannel(channel, value === true)
                        }
                      />
                      {CHANNEL_LABELS[channel]}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
